import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiDateField,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  RowProps,
} from "../../../../../components";
import { MuiSelectedList } from "../../../../../components/selectedList/MuiSelectedList";
import { StatusBadge } from "../../../../../components/statusBadge/StatusBadge";
import { MAX_LENGTH_75 } from "../../../../../helpers/constants";
import {
  checkFilterMatch,
  compareDates,
  convertToUserFormat,
  getFullName,
  getUniqueFilters,
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import { SearchParticipantResult } from "../../../../createBulkActivity/modelsAndSchemas/Models";
import {
  HeaderCheckboxProps,
  ModalState,
  ModalType,
  SearchParticipantsResultFormKey,
  UniqueParticipantResultFiltersTypes,
} from "../../../modelsAndSchema/Model";
import { bulkEditSearchParticipantResultsSiteCoreKeys } from "../../../siteCore/siteCoreKeys";
import { SearchParticipantsResultWrapper } from "../BulkEditSearchParticipant.style";

interface SearchParticipantsResultProps {
  rows: SearchParticipantResult[];
  pageId: string;
  setShowInformationModal?: (state: ModalState) => void;
  selectedClientId: string;
  setSelectedParticipants: (participants: SearchParticipantResult[]) => void;
}

const HeaderCheckbox: FC<HeaderCheckboxProps> = ({
  checked,
  onCheckedChange,
}) => {
  const { commonARIALabel } = useNavigationSiteCore();

  return (
    <MuiCheckbox
      checked={checked}
      checkboxLabel={`${commonARIALabel?.selectAll}`}
      hideLabel={true}
      onCheckedChange={onCheckedChange}
    />
  );
};

const getSearchParticipantsResultColumns = (
  isAllSelected: boolean,
  onHeaderCheckBoxChange: (checked: boolean) => void,
  handleRowCheckboxChange: (checked: boolean, id: string) => void,
  pageId: string,
  getLabel: (key: string) => string,
  userCountryDateFormat?: string,
) => [
  {
    id: SearchParticipantsResultFormKey.isSelected,
    label: "",
    className: "checkbox-cell",
    isSortVisible: false,
    headerRender: () => (
      <HeaderCheckbox
        checked={isAllSelected}
        onCheckedChange={onHeaderCheckBoxChange}
      />
    ),
    render: (row: RowProps) => {
      return (
        <MuiCheckbox
          checked={row.isSelected}
          checkboxLabel={`${row.name}`}
          hideLabel={true}
          id={`${pageId}-${row.participantId}`}
          onCheckedChange={(checked: boolean) => {
            handleRowCheckboxChange(checked, row.participantId);
          }}
          key={`${pageId}-${row.participantId}-${row.isSelected}`}
        />
      );
    },
  },
  {
    id: SearchParticipantsResultFormKey.participantName,
    label: getLabel(
      bulkEditSearchParticipantResultsSiteCoreKeys.participantName,
    ),
    isEmptyValue: true,
    isSortVisible: true,
  },
  {
    id: SearchParticipantsResultFormKey.clientName,
    label: getLabel(bulkEditSearchParticipantResultsSiteCoreKeys.clientName),
    className: "cell",
    isSortVisible: true,
    isEmptyValue: true,
  },
  {
    id: SearchParticipantsResultFormKey.participantKeyNumber,
    label: getLabel(bulkEditSearchParticipantResultsSiteCoreKeys.programKey),
    className: "cell",
    isSortVisible: true,
    isEmptyValue: true,
  },
  {
    id: SearchParticipantsResultFormKey.deliveryCountryName,
    label: getLabel(
      bulkEditSearchParticipantResultsSiteCoreKeys.deliveryCountry,
    ),
    className: "cell",
    isSortVisible: true,
    isEmptyValue: true,
  },
  {
    id: SearchParticipantsResultFormKey.lobName,
    label: getLabel(bulkEditSearchParticipantResultsSiteCoreKeys.productLine),
    className: "cell",
    isSortVisible: true,
    isEmptyValue: true,
  },
  {
    id: SearchParticipantsResultFormKey.programTemplateName,
    label: getLabel(
      bulkEditSearchParticipantResultsSiteCoreKeys.programTemplate,
    ),
    className: "cell",
    isSortVisible: true,
    isEmptyValue: true,
  },
  {
    id: SearchParticipantsResultFormKey.status,
    label: getLabel(bulkEditSearchParticipantResultsSiteCoreKeys.programStatus),
    isSortVisible: true,
    isEmptyValue: true,
    className: "cell",
    render: (row: RowProps) => (
      <StatusBadge status={row.statusKey} label={row.status} />
    ),
  },
  {
    id: SearchParticipantsResultFormKey.participantEmailAddress,
    label: getLabel(
      bulkEditSearchParticipantResultsSiteCoreKeys.participantEmail,
    ),
    className: "cell",
    isSortVisible: true,
    isEmptyValue: true,
  },
  {
    id: SearchParticipantsResultFormKey.createdDate,
    label: getLabel(bulkEditSearchParticipantResultsSiteCoreKeys.createdDate),
    className: "cell",
    isSortVisible: true,
    isEmptyValue: true,
    render: (row: RowProps) => {
      return <>{convertToUserFormat(row.createdDate, userCountryDateFormat)}</>;
    },
  },
];

export const SearchParticipantsResults: FC<SearchParticipantsResultProps> = ({
  rows,
  pageId,
  setShowInformationModal,
  selectedClientId,
  setSelectedParticipants,
}) => {
  const { useFetchLabel, commonLabels, fetchValueByKeyOfDropdown } =
    useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);

  const [allUpdatedRows, setAllUpdatedRows] = useState<
    SearchParticipantResult[]
  >([]);

  const [uniqueFilters, setUniqueFilters] =
    useState<UniqueParticipantResultFiltersTypes>(() => ({
      deliveryCountry: [],
      lobName: [],
      programTemplate: [],
      status: [],
    }));

  const getLabel = useFetchLabel(
    bulkEditSearchParticipantResultsSiteCoreKeys.participantsResultsKey,
  );

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const defaultSearchParticipantResult = {
    [SearchParticipantsResultFormKey.participantName]: "",
    [SearchParticipantsResultFormKey.clientName]: "",
    [SearchParticipantsResultFormKey.programTemplateName]: "",
    [SearchParticipantsResultFormKey.deliveryCountryName]: "",
    [SearchParticipantsResultFormKey.participantKeyNumber]: "",
    [SearchParticipantsResultFormKey.status]: "",
    [SearchParticipantsResultFormKey.lobName]: "",
    [SearchParticipantsResultFormKey.participantEmailAddress]: "",
    [SearchParticipantsResultFormKey.createdDate]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultSearchParticipantResult,
  });

  const { reset, control, getValues, setValue } = methods;

  const filters = useWatch({ control });

  useEffect(() => {
    resetPagination();
    reset();
  }, [rows]);

  useEffect(() => {
    if (!siteCoreLoading) {
      const mappedResults = rows.map((item) => mapRowToParticipantResult(item));

      setAllUpdatedRows(mappedResults);
      setFilteredResults([...mappedResults]);

      const uniqueCountries = getUniqueFilters(
        mappedResults,
        "deliveryCountryName",
      );

      const uniqueLineOfBusiness = getUniqueFilters(mappedResults, "lobName");

      const uniqueParticipantStatuses = getUniqueFilters(
        mappedResults,
        "status",
      );

      const uniqueProgramTemplates = getUniqueFilters(
        mappedResults,
        "programTemplateName",
      );

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        deliveryCountry: uniqueCountries,
        lobName: uniqueLineOfBusiness,
        programTemplate: uniqueProgramTemplates,
        status: uniqueParticipantStatuses,
      }));

      setIsAllSelected(false);
    }
  }, [rows, siteCoreLoading]);

  const mapRowToParticipantResult = (
    row: SearchParticipantResult,
  ): SearchParticipantResult => {
    return {
      ...row,
      participantName: getFullName(
        row.participantFirstName,
        row.participantLastName,
        true,
      ),
      statusKey: row.status,
      status: fetchValueByKeyOfDropdown(
        bulkEditSearchParticipantResultsSiteCoreKeys.participantsResultsKey,
        bulkEditSearchParticipantResultsSiteCoreKeys.participantStatusList,
        row.status,
      ),
      createdDate: row.createdTimeStamp,
      isSelected: false,
    };
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        participantName,
        clientName,
        participantKeyNumber,
        deliveryCountryName,
        lobName,
        programTemplateName,
        status,
        participantEmailAddress,
        createdDate,
      } = getValues();

      const myData = allUpdatedRows.filter((item: SearchParticipantResult) => {
        const participantNameMatch = checkFilterMatch(
          participantName,
          item.participantName ?? "",
        );

        const keyNumberMatch = checkFilterMatch(
          participantKeyNumber,
          item.participantKeyNumber ?? "",
        );

        const emailMatch = checkFilterMatch(
          participantEmailAddress,
          item.participantEmailAddress ?? "",
        );

        const companyNameMatch = checkFilterMatch(clientName, item.clientName);

        const lineOfBusinessMatch = checkFilterMatch(lobName, item.lobName);

        const programTemplateMatch = checkFilterMatch(
          programTemplateName,
          item.programTemplateName,
        );

        let isDateMatch = compareDates(
          createdDate ?? "",
          item?.createdDate ?? "",
          getValueOrBlank(userCountryDateFormat),
        );

        const statusMatch =
          status === "" || item.status?.toLowerCase() === status?.toLowerCase();

        const deliveryCountryMatch = checkFilterMatch(
          deliveryCountryName,
          item.deliveryCountryName,
        );

        return (
          participantNameMatch &&
          companyNameMatch &&
          keyNumberMatch &&
          lineOfBusinessMatch &&
          programTemplateMatch &&
          statusMatch &&
          deliveryCountryMatch &&
          emailMatch &&
          isDateMatch
        );
      });
      setFilteredResults([...myData]);
      resetPagination();
      setAllCheckboxSelected(myData);
    }
  }, [filters]);

  const setAllCheckboxSelected = (updatedRows: SearchParticipantResult[]) => {
    // Check if all rows have isSelected as true
    const allSelected = updatedRows.every(
      (row: SearchParticipantResult) => row.isSelected === true,
    );
    setIsAllSelected(updatedRows.length ? allSelected : false);
  };

  const handleRowCheckboxChange = (checked: boolean, rowId: string) => {
    // Update only the clicked row's `isSelected` value
    if (isNotEmpty(selectedClientId)) {
      const updatedRows = filteredResults.map((row: SearchParticipantResult) =>
        row.participantId === rowId ? { ...row, isSelected: checked } : row,
      );
      setFilteredResults([...updatedRows]);

      setAllCheckboxSelected(updatedRows);

      const updatedAllRows = allUpdatedRows.map((row) =>
        row.participantId === rowId ? { ...row, isSelected: checked } : row,
      );
      setAllUpdatedRows([...updatedAllRows]);
      updateSelectedDataToParent([...updatedAllRows]);
    } else {
      setShowInformationModal?.({
        type: ModalType.ClientValidation,
        show: true,
      } as ModalState);
    }
  };

  const onHeaderCheckBoxChange = (checked: boolean) => {
    if (isNotEmpty(selectedClientId)) {
      setIsAllSelected(checked);
      const updatedRowsWithCheckedStatus = filteredResults.map(
        (row: SearchParticipantResult) => ({
          ...row,
          isSelected: checked,
        }),
      );

      setFilteredResults([...updatedRowsWithCheckedStatus]);

      // Also update `allUpdatedRows` to reflect changes for the whole dataset
      const updatedAllRows = allUpdatedRows.map(
        (row: SearchParticipantResult) => {
          const matchedRow = updatedRowsWithCheckedStatus.find(
            (updatedRow) => updatedRow.participantId === row.participantId,
          );
          return matchedRow
            ? { ...row, isSelected: matchedRow.isSelected }
            : row;
        },
      );

      // Update `allUpdatedRows` state
      updateSelectedDataToParent([...updatedAllRows]);
      setAllUpdatedRows([...updatedAllRows]);
    } else {
      setShowInformationModal?.({
        type: ModalType.ClientValidation,
        show: true,
      } as ModalState);
    }
  };

  const updateSelectedDataToParent = (
    filteredResults: SearchParticipantResult[],
  ) => {
    // update selected participants in parent component
    setSelectedParticipants(filteredResults);
  };

  const handleRemove = (id: number | string) => {
    handleRowCheckboxChange(false, id as string);
  };

  const selectedItems = useMemo(() => {
    return allUpdatedRows
      .filter((participant) => participant.isSelected)
      .map((item) => ({
        label: item.participantName ?? "",
        value: item.participantId,
      }));
  }, [allUpdatedRows]);

  const columns = useMemo(
    () =>
      getSearchParticipantsResultColumns(
        isAllSelected,
        onHeaderCheckBoxChange,
        handleRowCheckboxChange,
        pageId,
        getLabel,
        userCountryDateFormat,
      ),
    [isAllSelected, onHeaderCheckBoxChange, handleRowCheckboxChange, pageId],
  );

  const tableHeaderForm = [
    <></>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.participantName}
      name={SearchParticipantsResultFormKey.participantName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantsResultFormKey.participantName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.clientName}
      name={SearchParticipantsResultFormKey.clientName}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantsResultFormKey.clientName}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.participantKeyNumber}
      name={SearchParticipantsResultFormKey.participantKeyNumber}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantsResultFormKey.participantKeyNumber}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.deliveryCountryName}
      name={SearchParticipantsResultFormKey.deliveryCountryName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={uniqueFilters.deliveryCountry}
        value=""
        id={`${pageId}-${SearchParticipantsResultFormKey.deliveryCountryName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
        showSearch
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.lobName}
      name={SearchParticipantsResultFormKey.lobName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueFilters.lobName}
        value=""
        id={`${pageId}-${SearchParticipantsResultFormKey.lobName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchParticipantsResultFormKey.programTemplateName}
      key={SearchParticipantsResultFormKey.programTemplateName}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueFilters.programTemplate}
        value=""
        id={`${pageId}-${SearchParticipantsResultFormKey.programTemplateName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.status}
      name={SearchParticipantsResultFormKey.status}
    >
      <MuiSelect
        label=""
        visibleDefaultSelectOption
        options={uniqueFilters.status}
        value=""
        id={`${pageId}-${SearchParticipantsResultFormKey.status}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.participantEmailAddress}
      name={SearchParticipantsResultFormKey.participantEmailAddress}
    >
      <MuiTextFieldWithSearchIcon
        id={`${pageId}-${SearchParticipantsResultFormKey.participantEmailAddress}`}
        placeholder={commonLabels.search}
        showCloseIcon
        maxLength={MAX_LENGTH_75}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={SearchParticipantsResultFormKey.createdDate}
      name={SearchParticipantsResultFormKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          name={SearchParticipantsResultFormKey.createdDate}
          clearable
          value={
            getValues(SearchParticipantsResultFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(SearchParticipantsResultFormKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SearchParticipantsResultFormKey.createdDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
  ];

  return (
    <SearchParticipantsResultWrapper>
      <MuiBox className="search-result-header-wrapper">
        <MuiTypography
          variant="h2SemiBold"
          className="search-result-header"
          component="div"
        >
          {getLabel(
            bulkEditSearchParticipantResultsSiteCoreKeys.chooseParticipantsSectionSubTitle,
          )}
        </MuiTypography>
        <MuiBox className="search-header-right">
          <MuiSelectedList
            selectedItems={selectedItems}
            onRemove={handleRemove}
            id="participants-select"
            label={replaceHashWithValue(
              getLabel(
                bulkEditSearchParticipantResultsSiteCoreKeys.participantsSectedDropDownText,
              ),
              String(selectedItems?.length ?? 0),
            )}
            requiredARIALabel={commonARIALabel.required}
          />
        </MuiBox>
      </MuiBox>

      <MuiTable
        ref={tableRef}
        pageId={pageId}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={[...filteredResults]}
        noDataInApi={filteredResults.length === 0}
        noResultMessage={commonLabels.noResult}
        priorityResults={[]}
        emptyMessageColSpan={8}
      />
    </SearchParticipantsResultWrapper>
  );
};
